// Floating UI placement (menus, tooltips) without a positioning library. Floating elements are `position: fixed` and,
// where the browser has the Popover API, live in the top layer (`popover="manual"`), so they escape the overflow
// clipping of tables and cards and stack above a modal <dialog>. They stay DOM descendants of their trigger's
// container, so a menu opened inside a modal dialog is not made inert by it.

import { useLayoutEffect, type RefObject } from 'react';

export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Align = 'start' | 'center' | 'end';
export type Placement = Side | `${Side}-${'start' | 'end'}`;

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface Position {
  top: number;
  left: number;
  /** The side actually used after flipping (for arrows and transform origins). */
  side: Side;
}

/** Distance kept between a floating element and the viewport edge. */
const EDGE = 8;

function splitPlacement(placement: Placement): [Side, Align] {
  const [side, align] = placement.split('-') as [Side, 'start' | 'end' | undefined];
  return [side, align ?? 'center'];
}

const OPPOSITE: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

function place(anchor: Box, size: { width: number; height: number }, side: Side, align: Align, offset: number) {
  let top: number;
  let left: number;
  if (side === 'top' || side === 'bottom') {
    top = side === 'bottom' ? anchor.top + anchor.height + offset : anchor.top - size.height - offset;
    if (align === 'start') left = anchor.left;
    else if (align === 'end') left = anchor.left + anchor.width - size.width;
    else left = anchor.left + (anchor.width - size.width) / 2;
  } else {
    left = side === 'right' ? anchor.left + anchor.width + offset : anchor.left - size.width - offset;
    if (align === 'start') top = anchor.top;
    else if (align === 'end') top = anchor.top + anchor.height - size.height;
    else top = anchor.top + (anchor.height - size.height) / 2;
  }
  return { top, left };
}

function fits(pos: { top: number; left: number }, size: { width: number; height: number }, viewport: Viewport, side: Side) {
  if (side === 'top') return pos.top >= EDGE;
  if (side === 'bottom') return pos.top + size.height <= viewport.height - EDGE;
  if (side === 'left') return pos.left >= EDGE;
  return pos.left + size.width <= viewport.width - EDGE;
}

/**
 * Where to put a floating box of `size` next to `anchor`: the requested side if it fits, else the opposite side if
 * that fits, else the requested side; then clamped so it stays inside the viewport. Pure, so it is unit-testable.
 */
export function computePosition(
  anchor: Box,
  size: { width: number; height: number },
  placement: Placement,
  viewport: Viewport,
  offset = 6,
): Position {
  const [requested, align] = splitPlacement(placement);
  let side = requested;
  let pos = place(anchor, size, side, align, offset);
  if (!fits(pos, size, viewport, side)) {
    const flipped = OPPOSITE[side];
    const alt = place(anchor, size, flipped, align, offset);
    if (fits(alt, size, viewport, flipped)) {
      side = flipped;
      pos = alt;
    }
  }
  const maxLeft = Math.max(EDGE, viewport.width - size.width - EDGE);
  const maxTop = Math.max(EDGE, viewport.height - size.height - EDGE);
  return {
    top: Math.round(Math.min(Math.max(pos.top, EDGE), maxTop)),
    left: Math.round(Math.min(Math.max(pos.left, EDGE), maxLeft)),
    side,
  };
}

/** True when this browser has the Popover API (top layer for non-modal floating UI). */
export function supportsPopover(): boolean {
  return typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';
}

function isPopoverOpen(el: HTMLElement): boolean {
  try {
    return el.matches(':popover-open');
  } catch {
    return false;
  }
}

/** Shows a `popover` element in the top layer (no-op without the API or the attribute). */
export function showPopoverSafe(el: HTMLElement | null): void {
  if (!el || !supportsPopover() || !el.hasAttribute('popover')) return;
  try {
    if (!isPopoverOpen(el)) el.showPopover();
  } catch {
    // An element that is not connected, or already shown by the browser; nothing to do.
  }
}

export function hidePopoverSafe(el: HTMLElement | null): void {
  if (!el || !supportsPopover() || !el.hasAttribute('popover')) return;
  try {
    if (isPopoverOpen(el)) el.hidePopover();
  } catch {
    // Already hidden or disconnected.
  }
}

/**
 * Re-adds a popover to the top layer so it sits above anything opened after it (toasts raised over a dialog that
 * opened later). The top layer is ordered by insertion, so hide + show moves it to the top.
 */
export function raisePopover(el: HTMLElement | null): void {
  if (!el || !supportsPopover() || !el.hasAttribute('popover')) return;
  hidePopoverSafe(el);
  showPopoverSafe(el);
}

/**
 * Keeps `floatingRef` positioned next to `anchorRef` while `open`: on open, on window resize, on any scroll (capture,
 * so scrolling a table body moves its row menu too) and when either element changes size. Writes `top`/`left`
 * straight to the element's style to avoid a render per scroll frame, and sets `data-side` for styling.
 */
export function useAnchoredPosition(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  floatingRef: RefObject<HTMLElement | null>,
  placement: Placement,
  offset = 6,
): void {
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const anchor = anchorRef.current;
      const floating = floatingRef.current;
      if (!anchor || !floating) return;
      const a = anchor.getBoundingClientRect();
      const size = { width: floating.offsetWidth, height: floating.offsetHeight };
      const pos = computePosition(
        { top: a.top, left: a.left, width: a.width, height: a.height },
        size,
        placement,
        { width: window.innerWidth, height: window.innerHeight },
        offset,
      );
      floating.style.top = `${pos.top}px`;
      floating.style.left = `${pos.left}px`;
      floating.dataset.side = pos.side;
    };
    update();
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    if (observer) {
      if (anchorRef.current) observer.observe(anchorRef.current);
      if (floatingRef.current) observer.observe(floatingRef.current);
    }
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      observer?.disconnect();
    };
  }, [open, anchorRef, floatingRef, placement, offset]);
}
